import { Drawer, TextField, Typography } from '@mui/material';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react'
import { axiosInstance } from '../../utility/apiInstance';
import toast from 'react-hot-toast';

interface WhatsAppDrawerProps {
    whatsAppDrawer: boolean,
    onClose: () => void,
    organizationId: any
}

const WhatsAppDrawer = ({ whatsAppDrawer, onClose, organizationId }: WhatsAppDrawerProps) => {
    const [organisationName, setOrgainsationName] = useState("");
    const [configId, setConfigId] = useState("");
    const [whatsappConfig, setWhatsappConfig] = useState({
        apiKey: '',
        campaignName: '',
        reScheduledcampaignName: '',
        footer: '',
    });
    const [errors, setErrors] = useState<any>({});
    const [activeTab, setActiveTab] = useState<'air' | 'ocean'>('air');

    // ✅ All fields are optional — empty array means no validation
    const requiredFields: string[] = [];

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        if (requiredFields.includes(name)) {
            if (!value.trim()) {
                setErrors((prev: any) => ({ ...prev, [name]: "This field is required" }));
            } else {
                setErrors((prev: any) => {
                    const newErrors = { ...prev };
                    delete newErrors[name];
                    return newErrors;
                });
            }
        }
        setWhatsappConfig((prev) => ({ ...prev, [name]: value }));
    };

    const handleTabChange = (tab: 'air' | 'ocean') => {
        setActiveTab(tab);
        setConfigId("");
        setWhatsappConfig({ apiKey: '', campaignName: '', reScheduledcampaignName: '', footer: '' });
        setErrors({});
        if (organizationId) fetchWhatsappConfig(organizationId, tab);
    };

    const onHandleSave = () => {
        if (configId && organizationId) {
            updateAWhatsappConfig(organizationId);
        } else {
            createAWhatsappConfig(organizationId);
        }
    };

    useEffect(() => {
        if (!organizationId) return;
        getAOrgainsationDetailByID(organizationId);
        fetchWhatsappConfig(organizationId, activeTab);
    }, [organizationId]);

    const getAOrgainsationDetailByID = async (organizationId: any) => {
        if (!organizationId) { toast.error("Organization ID is Required"); return; }
        try {
            const response = await axiosInstance.get(`/api/v1/organisation/getById/${organizationId}`);
            setOrgainsationName(response?.data?.data?.organizationName);
        } catch (error) {
            console.error("Error fetching organisation:", error);
        }
    };

    const fetchWhatsappConfig = async (organizationId: string, service: 'air' | 'ocean') => {
        try {
            const response = await axiosInstance.get(
                `/api/v1/whatsappconfig/getawhatsappconfig/${organizationId}`,
                { params: { service } }
            );
            const d = response?.data?.data;
            if (!d) return;
            setWhatsappConfig({
                apiKey: d.apiKey || '',
                campaignName: d.campaignName || '',
                reScheduledcampaignName: d.reScheduledcampaignName || '',
                footer: d.footer || '',
            });
            setConfigId(d._id);
        } catch (error) {
            console.error("Fetch whatsapp config error:", error);
        }
    };

    const validateAllFields = () => {
        const newErrors: any = {};
        requiredFields.forEach((field) => {
            const value = whatsappConfig[field as keyof typeof whatsappConfig];
            if (!value?.toString().trim()) newErrors[field] = "This field is required";
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const buildPayload = () => ({
        apiKey: whatsappConfig.apiKey,
        campaignName: whatsappConfig.campaignName,
        reScheduledcampaignName: whatsappConfig.reScheduledcampaignName,
        service: activeTab,
        ...(whatsappConfig.footer && { footer: whatsappConfig.footer }),
    });

    const handleApiErrors = (error: any) => {
        const apiErrors = error?.response?.data?.error;
        if (Array.isArray(apiErrors) && apiErrors.length > 0) {
            const fieldErrors: any = {};
            apiErrors.forEach((err: { field: string; message: string }) => {
                fieldErrors[err.field] = err.message;
            });
            setErrors((prev: any) => ({ ...prev, ...fieldErrors }));
        } else {
            toast.error(error?.message || "Something went wrong");
        }
    };

    const createAWhatsappConfig = async (organizationId: string) => {
        if (!validateAllFields()) return;
        try {
            const response = await axiosInstance.post(`/api/v1/whatsappconfig/create/${organizationId}`, buildPayload());
            if (response?.data?.success) { toast.success("Successfully Created"); }
        } catch (error: any) { handleApiErrors(error); }
    };

    const updateAWhatsappConfig = async (organizationId: string) => {
        if (!validateAllFields()) return;
        try {
            const response = await axiosInstance.post(`/api/v1/whatsappconfig/update/${organizationId}`, buildPayload());
            if (response?.data?.success) { toast.success("Successfully Updated"); }
        } catch (error: any) { handleApiErrors(error); }
    };

    const labelSx = { mb: '2px', fontSize: '12px', fontWeight: 500, color: 'text.secondary' };

    return (
        <Drawer
            anchor="right"
            open={whatsAppDrawer}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 400 },
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden',
                },
            }}
        >
            {/* Header */}
            <div style={{
                backgroundColor: '#F8B71F',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
            }}>
                <span style={{ fontSize: '15px', fontWeight: 600, color: '#fff' }}>
                    WhatsApp Configuration
                </span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
                    <X size={18} color="#fff" />
                </button>
            </div>

            {/* Air / Ocean Tab */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                {(['air', 'ocean'] as const).map((tab) => (
                    <button
                        key={tab}
                        onClick={() => handleTabChange(tab)}
                        style={{
                            flex: 1,
                            padding: '9px 0',
                            fontSize: '13px',
                            fontWeight: activeTab === tab ? 600 : 400,
                            color: activeTab === tab ? '#F8B71F' : '#6b7280',
                            background: 'transparent',
                            border: 'none',
                            borderBottom: activeTab === tab ? '2px solid #F8B71F' : '2px solid transparent',
                            cursor: 'pointer',
                            textTransform: 'capitalize',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        {tab === 'air' ? 'Air' : 'Ocean'}
                    </button>
                ))}
            </div>

            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>

                <div>
                    <Typography sx={labelSx}>Organisation Name</Typography>
                    <TextField disabled fullWidth size="small" value={organisationName} placeholder="Organisation Name" />
                </div>

                {/* ✅ No * on any label — all optional */}
                <div>
                    <Typography sx={labelSx}>API Key</Typography>
                    <TextField
                        fullWidth name="apiKey" placeholder="API Key"
                        value={whatsappConfig.apiKey} onChange={handleChange}
                        multiline rows={2}
                        error={!!errors?.apiKey} helperText={errors?.apiKey}
                    />
                </div>

                <div>
                    <Typography sx={labelSx}>Campaign Name</Typography>
                    <TextField
                        fullWidth size="small" name="campaignName" placeholder="Campaign Name"
                        value={whatsappConfig.campaignName} onChange={handleChange}
                        inputProps={{ maxLength: 100 }}
                        error={!!errors?.campaignName} helperText={errors?.campaignName}
                    />
                </div>

                <div>
                    <Typography sx={labelSx}>Rescheduled Campaign Name</Typography>
                    <TextField
                        fullWidth size="small" name="reScheduledcampaignName"
                        placeholder="Rescheduled Campaign Name"
                        value={whatsappConfig.reScheduledcampaignName} onChange={handleChange}
                        inputProps={{ maxLength: 100 }}
                        error={!!errors?.reScheduledcampaignName} helperText={errors?.reScheduledcampaignName}
                    />
                </div>

                <div>
                    <Typography sx={labelSx}>Footer</Typography>
                    <TextField
                        fullWidth size="small" name="footer" placeholder="Footer"
                        value={whatsappConfig.footer} onChange={handleChange}
                        inputProps={{ maxLength: 255 }}
                        error={!!errors?.footer} helperText={errors?.footer}
                    />
                </div>

            </div>

            {/* Footer buttons */}
            <div style={{
                padding: '10px 16px',
                borderTop: '1px solid #eee',
                display: 'flex',
                gap: 8,
                flexShrink: 0,
            }}>
                <button className="btn btnBlack" onClick={onClose}>Cancel</button>
                <button className="btn btnYellow" onClick={onHandleSave}>
                    {configId ? 'Update' : 'Save'}
                </button>
            </div>

        </Drawer>
    );
};

export default WhatsAppDrawer;